import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { DashboardSummary, HeatmapCategory, Deadline } from '../types';
import { ScoreGauge } from '../components/dashboard/ScoreGauge';
import { MaturityBadge } from '../components/dashboard/MaturityBadge';
import { CategoryHeatmap } from '../components/dashboard/CategoryHeatmap';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapCategory[]>([]);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [trends, setTrends] = useState<any[]>([]);
  const [risks, setRisks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, h, d, t, r] = await Promise.all([
          dashboardAPI.summary(),
          dashboardAPI.heatmap(),
          dashboardAPI.deadlines(),
          dashboardAPI.trends(),
          dashboardAPI.risks(),
        ]);
        setSummary(s.data);
        setHeatmap(h.data.categories || []);
        setDeadlines(d.data.deadlines || []);
        setTrends(t.data.trend || []);
        setRisks(r.data.risks || []);
      } catch (e) {
        console.error('Dashboard load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">POSH Governance Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Prevention of Sexual Harassment — Compliance Overview</p>
        </div>
        <button
          onClick={() => navigate('/app/assessment')}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          + New Assessment
        </button>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Score gauge */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center">
          <h2 className="text-sm font-semibold text-gray-600 mb-4">Overall POSH Score</h2>
          <ScoreGauge score={summary?.overall_score ?? 0} size={180} />
          <p className="mt-3 text-xs text-gray-400">Weighted across all 5 compliance categories</p>
        </div>

        {/* Maturity badge */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center justify-center gap-4">
          <h2 className="text-sm font-semibold text-gray-600">Maturity Level</h2>
          <MaturityBadge
            level={summary?.maturity_level || 'N/A'}
            label={summary?.maturity_label || 'Not Assessed'}
            large
          />
          <div className="text-center">
            <p className="text-xs text-gray-500">Total Assessments: <strong>{summary?.total_assessments ?? 0}</strong></p>
            <p className="text-xs text-gray-500">Drafts: <strong>{summary?.draft_assessments ?? 0}</strong></p>
          </div>
        </div>

        {/* Quick stats */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-600">Compliance Deadlines</h2>
          {deadlines.length === 0 && (
            <p className="text-xs text-gray-400">No deadlines found.</p>
          )}
          {deadlines.slice(0, 3).map((d, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`mt-0.5 text-xs px-1.5 py-0.5 rounded font-medium ${
                d.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {d.days_remaining < 0 ? `${Math.abs(d.days_remaining)}d overdue` : `${d.days_remaining}d`}
              </span>
              <div>
                <p className="text-xs font-medium text-gray-800">{d.label}</p>
                <p className="text-xs text-gray-400">{d.due_date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Heatmap + Risks row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Category Compliance Heatmap</h2>
          <CategoryHeatmap categories={heatmap} />
          {heatmap.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">
              No assessment data yet. <button onClick={() => navigate('/assessment')} className="text-blue-600 underline">Start an assessment →</button>
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Risk Exposure</h2>
          {risks.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-sm text-green-600 font-medium">✅ No high-risk categories detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {risks.map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                    r.risk_level === 'HIGH' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`}>{r.risk_level}</span>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-800">{r.category}</p>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                      <div
                        className={`h-1.5 rounded-full ${r.risk_level === 'HIGH' ? 'bg-red-400' : 'bg-amber-400'}`}
                        style={{ width: `${r.score}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">{r.score.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Trend chart */}
      {trends.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Score Trend Over Time</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                tick={{ fontSize: 11 }}
              />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [`${v.toFixed(1)}%`, 'Score']} />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#2563eb"
                strokeWidth={2}
                dot={{ fill: '#2563eb', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
